// glassSurfaces.jsx
import * as React from "react"
import Paper from "@mui/material/Paper"
import Card from "@mui/material/Card"
import { styled } from "@mui/material/styles"

// Shared props for tuning the glass look
const glassPropsBlock = ["blur", "opacity", "tint", "shadow", "radius"]
const shouldForwardProp = (prop) => !glassPropsBlock.includes(prop)

/**
 * createGlassStyles
 *  - blur: px (default 16)
 *  - opacity: 0..1 (default 0.6)
 *  - tint: "light" | "dark" | undefined (defaults to theme mode)
 *  - shadow: "none" | "sm" | "md" (default "sm")
 *  - radius: multiplier applied to theme.shape.borderRadius (default 2)
 */
const createGlassStyles = (
  theme,
  { blur = 16, opacity = 0.6, tint, shadow = "sm", radius = 2 } = {}
) => {
  const isDark = theme.palette.mode === "dark"
  const chosenTint = tint ?? (isDark ? "dark" : "light")
  const bgRGB = chosenTint === "light" ? "255,255,255" : "18,18,18"
  const borderRGBA =
    chosenTint === "light" ? "255,255,255,0.35" : "255,255,255,0.12"

  // compact drop shadows
  const dropShadow =
    shadow === "none"
      ? "none"
      : shadow === "md"
      ? chosenTint === "light"
        ? "0 6px 18px rgba(0,0,0,0.08)"
        : "0 6px 18px rgba(0,0,0,0.28)"
      : chosenTint === "light"
      ? "0 4px 12px rgba(0,0,0,0.06)"
      : "0 4px 12px rgba(0,0,0,0.25)"

  return {
    position: "relative",
    backgroundColor: `rgba(${bgRGB}, ${opacity})`,
    backdropFilter: `saturate(180%) blur(${blur}px)`,
    WebkitBackdropFilter: `saturate(180%) blur(${blur}px)`,
    boxShadow: `${dropShadow}, inset 0 1px 0 rgba(255,255,255,${
      chosenTint === "light" ? 0.35 : 0.08
    })`,
    border: `1px solid rgba(${borderRGBA})`,
    borderRadius: theme.shape.borderRadius * radius,
    // reasonable padding defaults; Card content still controls its own spacing
    "@supports not ((-webkit-backdrop-filter: none) or (backdrop-filter: none))":
      {
        backgroundColor: `rgba(${bgRGB}, ${Math.min(opacity + 0.2, 1)})`,
      },
  }
}

// Base Glass surface – handy if you ever want a div version
export const GlassSurface = styled("div", { shouldForwardProp })(
  ({ theme, blur, opacity, tint, shadow, radius }) =>
    createGlassStyles(theme, { blur, opacity, tint, shadow, radius })
)

// GlassPaper
export const GlassPaper = styled(Paper, { shouldForwardProp })(
  ({ theme, blur, opacity, tint, shadow, radius }) => ({
    ...createGlassStyles(theme, { blur, opacity, tint, shadow, radius }),
    padding: theme.spacing(2.5),
  })
)

// GlassCard
export const GlassCard = styled(Card, { shouldForwardProp })(
  ({ theme, blur, opacity, tint, shadow, radius }) => ({
    ...createGlassStyles(theme, { blur, opacity, tint, shadow, radius }),
    // Cards usually have their own spacing via CardContent—no default padding here.
    overflow: "hidden", // keeps the glass border neat with media
  })
)
